import { useCallback, useEffect } from "react";
import { roleManagementStore } from "../../stores/RoleManagement";
import { userStore } from "../../stores/User";
import { RoleManagementService } from "../../../services/supabase/RoleManagementService";

export const usePermissions = (calledFrom?: string) => {
    const { effectivePermissions } = roleManagementStore();
    const { user } = userStore();

    // Fetch permissions when user changes
    useEffect(() => {
        if (user.authenticated && user.info.id && calledFrom) {
            // Call service directly without destructuring to avoid re-renders
            RoleManagementService().getMyPermissions();
        } else if (calledFrom) {
            console.log(`⚠️ [usePermissions] Cannot fetch permissions - called from: ${calledFrom}`, {
                authenticated: user.authenticated,
                hasUserId: !!user.info?.id
            });
        }
    }, [user.authenticated, user.info.id, calledFrom]);

    const hasScreenAccess = useCallback((screenName: string): boolean => {
        // Normalize to lowercase for case-insensitive matching
        const normalizedScreenName = screenName.toLowerCase();
        const hasAccess = effectivePermissions?.allowedScreens.includes(normalizedScreenName) || false;
        return hasAccess;
    }, [effectivePermissions]);

    const hasScreenPermission = useCallback((screenName: string, permission: "view" | "create" | "update" | "delete"): boolean => {
        // Normalize to lowercase for case-insensitive matching
        const normalizedScreenName = screenName.toLowerCase();
        const screenPerms = effectivePermissions?.screenPermissions[normalizedScreenName];
        if (!screenPerms) {
            return false;
        }

        let result = false;
        switch (permission) {
            case "view": result = screenPerms.canView; break;
            case "create": result = screenPerms.canCreate; break;
            case "update": result = screenPerms.canUpdate; break;
            case "delete": result = screenPerms.canDelete; break;
            default: result = false;
        }
        return result;
    }, [effectivePermissions]);

    // Convenience methods
    const canViewScreen = useCallback((screenName: string) => hasScreenPermission(screenName, "view"), [hasScreenPermission]);
    const canCreateIn = useCallback((screenName: string) => hasScreenPermission(screenName, "create"), [hasScreenPermission]);
    const canUpdateIn = useCallback((screenName: string) => hasScreenPermission(screenName, "update"), [hasScreenPermission]);
    const canDeleteIn = useCallback((screenName: string) => hasScreenPermission(screenName, "delete"), [hasScreenPermission]);

    return {
        effectivePermissions,
        hasScreenAccess,
        hasScreenPermission,
        canViewScreen,
        canCreateIn,
        canUpdateIn,
        canDeleteIn,
        isLoading: roleManagementStore.getState().isLoading
    };
}; 